import React, { useCallback, useMemo, useState } from 'react';
import {
  Plus, Loader2, X, Trash2, MessageSquare, Layers, ChevronRight, ChevronDown,
  Check, CheckSquare, ListChecks, Square,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { RoundTimeline } from './RoundTimeline';
import { ToolApprovalCard } from './ToolApprovalCard';
import { MemoryPanel } from './MemoryPanel';
import { ProjectsPanel } from './projects/ProjectsPanel';
import { formatRelativeTime } from './chatFormat';
import { SkillsPanel } from './SkillsPanel';
import { ConnectorsPanel } from './ConnectorsPanel';
import { RoutinesPanel } from './routines/RoutinesPanel';
import type { PendingApproval, RoundEvent, WorkspaceConversation, WorkspaceUiArtifact } from './types';
import type { SideSection } from './SideNav';
import type { PaRoutine } from '@easyflow/shared';

type ConvGroupKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'older';
const CONV_GROUP_ORDER: ConvGroupKey[] = ['today', 'yesterday', 'last7', 'last30', 'older'];
const CONV_GROUP_LABEL: Record<ConvGroupKey, string> = {
  today: 'Oggi',
  yesterday: 'Ieri',
  last7: 'Ultimi 7 giorni',
  last30: 'Ultimi 30 giorni',
  older: 'Più vecchie',
};

function getConvGroupKey(iso?: string): ConvGroupKey {
  if (!iso) return 'older';
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return 'older';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfLast7 = startOfToday - 6 * 86_400_000;
  const startOfLast30 = startOfToday - 29 * 86_400_000;
  if (t >= startOfToday) return 'today';
  if (t >= startOfYesterday) return 'yesterday';
  if (t >= startOfLast7) return 'last7';
  if (t >= startOfLast30) return 'last30';
  return 'older';
}

export interface WorkspaceSidePanelProps {
  section: SideSection;

  conversations: WorkspaceConversation[];
  currentConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  onBulkDeleteConversations: (ids: string[]) => Promise<void>;

  rounds: RoundEvent[];

  artifacts: WorkspaceUiArtifact[];
  selectedArtifactId: string | null;
  onSelectArtifact: (id: string) => void;
  onDeleteArtifact?: (id: string) => void | Promise<void>;
  /** Desktop: toggle del pannello transitorio di destra. Mobile: omesso. */
  onToggleArtifactPanel?: () => void;
  artifactPanelOpen?: boolean;

  approvals: PendingApproval[];
  onDecideApproval: (id: string, decision: 'approved' | 'rejected', notes?: string, overrideArgs?: Record<string, unknown>) => Promise<void>;

  onOpenRoutineInChat: (routine?: PaRoutine) => void;

  assistantId: string;

  // Task #3822 — sezione Progetti
  /** Utente corrente: distingue le conversazioni proprie da quelle degli altri membri. */
  currentUserId?: string | null;
  /** Crea una nuova conversazione dentro il progetto e la apre in chat. */
  onNewProjectConversation?: (projectId: string) => Promise<void> | void;
}

// Contenuto delle sezioni del pannello laterale (single source of truth):
// rounds / artifacts / approvals / memory / lista conversazioni, con tutto lo
// stato UI locale (selezione multipla, conferme inline, gruppi collassabili).
// Il frame desktop lo monta nel drawer; il frame mobile in un overlay full-screen.
export const WorkspaceSidePanel: React.FC<WorkspaceSidePanelProps> = ({
  section,
  conversations, currentConversationId, onSelectConversation, onNewConversation, onDeleteConversation, onBulkDeleteConversations,
  rounds,
  artifacts, selectedArtifactId, onSelectArtifact, onDeleteArtifact, onToggleArtifactPanel, artifactPanelOpen = false,
  approvals, onDecideApproval,
  onOpenRoutineInChat,
  assistantId,
  currentUserId = null, onNewProjectConversation,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteArtifactId, setConfirmDeleteArtifactId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedConvIds, setSelectedConvIds] = useState<Set<string>>(() => new Set<string>());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState<boolean>(false);
  const [bulkDeleting, setBulkDeleting] = useState<boolean>(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<ConvGroupKey>>(
    () => new Set<ConvGroupKey>(CONV_GROUP_ORDER.filter(gk => gk !== 'today')),
  );

  // Task #3822 — le conversazioni dentro un progetto vivono nella sezione
  // Progetti: la lista chat mostra SOLO quelle fuori progetto (default invariato).
  const chatConversations = useMemo(() => conversations.filter(c => !c.project_id), [conversations]);

  const handleConfirmDelete = (id: string) => {
    if (confirmDeleteId === id) {
      onDeleteConversation(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
      window.setTimeout(() => {
        setConfirmDeleteId(prev => (prev === id ? null : prev));
      }, 4000);
    }
  };

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedConvIds(new Set<string>());
    setConfirmBulkDelete(false);
  }, []);

  const toggleConvSelection = useCallback((id: string) => {
    setSelectedConvIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setConfirmBulkDelete(false);
  }, []);

  const allSelected = chatConversations.length > 0 && selectedConvIds.size === chatConversations.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedConvIds(prev => {
      if (prev.size === chatConversations.length) return new Set<string>();
      return new Set(chatConversations.map(c => c.id));
    });
    setConfirmBulkDelete(false);
  }, [chatConversations]);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (selectedConvIds.size === 0) return;
    if (!confirmBulkDelete) {
      setConfirmBulkDelete(true);
      window.setTimeout(() => setConfirmBulkDelete(false), 4000);
      return;
    }
    setBulkDeleting(true);
    try {
      await onBulkDeleteConversations(Array.from(selectedConvIds));
      exitSelectionMode();
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedConvIds, confirmBulkDelete, onBulkDeleteConversations, exitSelectionMode]);

  const handleConfirmDeleteArtifact = (id: string) => {
    if (confirmDeleteArtifactId === id) {
      setConfirmDeleteArtifactId(null);
      void onDeleteArtifact?.(id);
    } else {
      setConfirmDeleteArtifactId(id);
      window.setTimeout(() => {
        setConfirmDeleteArtifactId(prev => (prev === id ? null : prev));
      }, 4000);
    }
  };

  if (section === 'projects') {
    return (
      <ProjectsPanel
        onSelectConversation={onSelectConversation}
        onNewProjectConversation={onNewProjectConversation}
        currentUserId={currentUserId}
      />
    );
  }
  if (section === 'routines') {
    return (
      <div className="h-full min-h-0">
        <RoutinesPanel onOpenInChat={onOpenRoutineInChat} />
      </div>
    );
  }
  if (section === 'rounds') {
    return (
      <ScrollArea className="h-full">
        <div className="px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground">Attività agente</div>
        <RoundTimeline rounds={rounds} />
      </ScrollArea>
    );
  }
  if (section === 'artifacts') {
    return (
      <ScrollArea className="h-full [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="px-3 py-2 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase text-muted-foreground">Artefatti ({artifacts.length})</span>
          {artifacts.length > 0 && onToggleArtifactPanel && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={onToggleArtifactPanel} title={artifactPanelOpen ? 'Nascondi pannello' : 'Mostra pannello'}>
              <Layers className="h-3 w-3" />
            </Button>
          )}
        </div>
        <ul className="px-2 pb-2">
          {artifacts.map(a => {
            const isConfirming = confirmDeleteArtifactId === a.id;
            return (
              <li key={a.id ?? a.slug}>
                <div
                  className={`group flex items-center gap-1.5 px-2 py-1.5 rounded text-xs cursor-pointer ${
                    selectedArtifactId === a.id ? 'bg-violet-50 text-violet-900' : 'text-foreground hover:bg-muted'
                  }`}
                  onClick={() => onSelectArtifact(a.id)}
                >
                  <ChevronRight className={`h-3 w-3 shrink-0 ${selectedArtifactId === a.id ? 'text-violet-600' : 'text-muted-foreground'}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate" title={a.title}>{a.title}</span>
                    <span className="block text-[10px] text-muted-foreground truncate">{a.kind} · v{a.version}{a.updatedAt ? ` · ${formatRelativeTime(a.updatedAt)}` : ''}</span>
                  </span>
                  {a.isFresh && <span className="w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />}
                  {a.id && onDeleteArtifact && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleConfirmDeleteArtifact(a.id); }}
                      aria-label={isConfirming ? 'Conferma eliminazione' : 'Elimina artefatto'}
                      title={isConfirming ? 'Conferma eliminazione' : 'Elimina'}
                      className={`shrink-0 h-6 min-w-[24px] px-1.5 rounded border text-[10px] inline-flex items-center justify-center gap-1 transition ${
                        isConfirming
                          ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                          : 'bg-card border-border text-muted-foreground hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                      }`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {isConfirming && <span>Conferma</span>}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
          {artifacts.length === 0 && <li className="text-xs text-muted-foreground px-2 py-3">Nessun artefatto</li>}
        </ul>
      </ScrollArea>
    );
  }
  if (section === 'approvals') {
    return (
      <ScrollArea className="h-full">
        <div className="px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground">Conferme ({approvals.length})</div>
        <div className="space-y-2 px-2 pb-2">
          {approvals.length === 0
            ? <div className="text-xs text-muted-foreground px-2 py-3">Nessuna conferma in sospeso</div>
            : approvals.map(a => (
                <ToolApprovalCard key={a.id} approval={a} onDecide={(d, n, o) => onDecideApproval(a.id, d, n, o)} />
              ))}
        </div>
      </ScrollArea>
    );
  }
  if (section === 'memory') {
    return (
      <div className="h-full min-h-0">
        <MemoryPanel assistantId={assistantId} />
      </div>
    );
  }
  // Task #3821 — Skills (procedure/playbook): confine skill ≠ memoria.
  if (section === 'skills') {
    return (
      <div className="h-full min-h-0">
        <SkillsPanel assistantId={assistantId} currentConversationId={currentConversationId} />
      </div>
    );
  }
  // Task #3818 — Connettori: visibilità perimetro effettivo + restrizione per-chat.
  if (section === 'connectors') {
    return (
      <div className="h-full min-h-0">
        <ConnectorsPanel assistantId={assistantId} currentConversationId={currentConversationId} />
      </div>
    );
  }
  // chat = conversation list (raggruppata per data, gruppi collassabili, trash sempre visibile)
  const grouped: Record<ConvGroupKey, WorkspaceConversation[]> = {
    today: [], yesterday: [], last7: [], last30: [], older: [],
  };
  for (const c of chatConversations) {
    const ts = c.last_message_at ?? c.updated_at ?? c.created_at;
    grouped[getConvGroupKey(ts)].push(c);
  }
  const toggleGroup = (k: ConvGroupKey) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  return (
    // Header, barra "seleziona tutto" e barra bulk vivono FUORI dalla
    // ScrollArea: il Viewport di Radix è `display:table`, dove `position:sticky`
    // non aggancia. Layout flex-col a piena altezza con la sola lista scrollabile.
    <div className="flex h-full flex-col">
      <div className="px-3 py-2 flex items-center justify-between bg-card border-b border-border shrink-0">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground">Conversazioni</span>
        {selectionMode ? (
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={exitSelectionMode}>
            <X className="h-3 w-3 mr-0.5" /> Annulla
          </Button>
        ) : (
          <div className="flex items-center gap-0.5">
            {chatConversations.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setSelectionMode(true)}
                title="Selezione multipla"
                aria-label="Selezione multipla"
              >
                <ListChecks className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onNewConversation}
              title="Nuova conversazione"
              aria-label="Nuova conversazione"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      {selectionMode && (
        <div className="px-2 py-1.5 bg-card border-b border-border flex items-center justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={toggleSelectAll}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            {allSelected
              ? <CheckSquare className="h-3.5 w-3.5 text-violet-600" />
              : <Square className="h-3.5 w-3.5 text-muted-foreground" />}
            {allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}
          </button>
          <span className="text-[10px] text-muted-foreground">{selectedConvIds.size} selezionate</span>
        </div>
      )}
      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div className="px-1 pb-2">
        {chatConversations.length === 0 && (
          <div className="text-xs text-muted-foreground px-2 py-3 text-center">Nessuna conversazione</div>
        )}
        {CONV_GROUP_ORDER.map(gk => {
          const items = grouped[gk];
          if (items.length === 0) return null;
          const isCollapsed = collapsedGroups.has(gk);
          return (
            <div key={gk} className="mt-1">
              <button
                type="button"
                onClick={() => toggleGroup(gk)}
                className="w-full flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted rounded"
              >
                {isCollapsed
                  ? <ChevronRight className="h-3 w-3 shrink-0" />
                  : <ChevronDown className="h-3 w-3 shrink-0" />}
                <span className="flex-1 text-left truncate">{CONV_GROUP_LABEL[gk]}</span>
                <span className="text-[10px] font-normal text-muted-foreground shrink-0">{items.length}</span>
              </button>
              {!isCollapsed && (
                <ul>
                  {items.map(c => {
                    const isActive = currentConversationId === c.id;
                    const isConfirming = confirmDeleteId === c.id;
                    const isSelected = selectedConvIds.has(c.id);
                    const fullTitle = c.title || 'Senza titolo';
                    const lastTs = c.last_message_at ?? c.updated_at;
                    const subline = c.created_at && lastTs && lastTs !== c.created_at
                      ? `creato ${formatRelativeTime(c.created_at)} · ultimo msg ${formatRelativeTime(lastTs)}`
                      : c.created_at
                        ? `creato ${formatRelativeTime(c.created_at)}`
                        : lastTs
                          ? `ultimo msg ${formatRelativeTime(lastTs)}`
                          : '';
                    return (
                      <li key={c.id}>
                        <div
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs cursor-pointer ${
                            selectionMode && isSelected
                              ? 'bg-violet-50 text-violet-900'
                              : isActive && !selectionMode
                                ? 'bg-violet-50 text-violet-900'
                                : 'text-foreground hover:bg-muted'
                          }`}
                          onClick={() => selectionMode ? toggleConvSelection(c.id) : onSelectConversation(c.id)}
                        >
                          {selectionMode ? (
                            <span
                              className={`shrink-0 h-3.5 w-3.5 rounded border inline-flex items-center justify-center ${
                                isSelected ? 'bg-violet-600 border-violet-600 text-white' : 'bg-card border-border'
                              }`}
                            >
                              {isSelected && <Check className="h-2.5 w-2.5" />}
                            </span>
                          ) : (
                            <MessageSquare className="h-3 w-3 shrink-0 text-muted-foreground" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="truncate" title={fullTitle}>{fullTitle}</div>
                            {subline && <div className="text-[10px] text-muted-foreground leading-tight">{subline}</div>}
                          </div>
                          {!selectionMode && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleConfirmDelete(c.id); }}
                              aria-label={isConfirming ? 'Conferma eliminazione' : 'Elimina conversazione'}
                              className={`shrink-0 h-6 min-w-[24px] px-1.5 rounded border text-[10px] inline-flex items-center justify-center gap-1 transition ${
                                isConfirming
                                  ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                                  : isActive
                                    ? 'bg-white/70 border-violet-200 text-violet-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                                    : 'bg-card border-border text-muted-foreground hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                              }`}
                              title={isConfirming ? 'Conferma eliminazione' : 'Elimina'}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {isConfirming && <span>Conferma</span>}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      </ScrollArea>
      {selectionMode && selectedConvIds.size > 0 && (
        <div className="bg-card border-t border-border px-2 py-2 flex items-center justify-between gap-2 shrink-0">
          <span className="text-[11px] text-muted-foreground">{selectedConvIds.size} selezionate</span>
          <button
            type="button"
            onClick={handleConfirmBulkDelete}
            disabled={bulkDeleting}
            className={`h-7 px-2.5 rounded border text-[11px] inline-flex items-center justify-center gap-1 transition disabled:opacity-60 ${
              confirmBulkDelete
                ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                : 'bg-card border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
            }`}
            title={confirmBulkDelete ? 'Conferma eliminazione' : 'Elimina selezionate'}
          >
            {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {confirmBulkDelete ? `Conferma (${selectedConvIds.size})` : `Elimina (${selectedConvIds.size})`}
          </button>
        </div>
      )}
    </div>
  );
};
